import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
    intl,
    DmsModal,
    DmsButton,
    fetchUrl,
    fetchData,
    HTTP_STATUS,
} from "dmsbasereact";
import {
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Button,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import Swal from "sweetalert2";
import { DocumentUploadSlot } from "../../components/DocumentUploadSlot/DocumentUploadSlot";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";
import { DmsRichTextEditor } from "../../components/DmsRichTextEditor";
import { downloadFile } from "../../utils/fileDownload";
import { FINAL_STATUSES } from "./constants";

export const AnnotationFormModal = ({
    open,
    onClose,
    onSaved,
    noticePk,
    annotation,
    statusOptions,
    lastStatus,
}) => {
    const isEdit = !!annotation;

    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [status, setStatus] = useState("");
    const [existingFiles, setExistingFiles] = useState([]);
    const [fileSlots, setFileSlots] = useState([{ id: 0, file: null }]);
    const [saving, setSaving] = useState(false);
    const [slotCounter, setSlotCounter] = useState(1);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [editorKey, setEditorKey] = useState(0);

    useEffect(() => {
        if (open) {
            setEditorKey((k) => k + 1);
            if (isEdit) {
                setTitle(annotation.title || "");
                setText(annotation.text || "");
                const statusVal = annotation.status?.value !== undefined
                    ? annotation.status.value
                    : annotation.status;
                setStatus(statusVal ?? "");
                const files = (annotation.files || []).map((f) => ({
                    pk: f.pk,
                    file_pk: f.file_pk,
                    original_filename: f.file?.original_filename || `File ${f.file_pk}`,
                }));
                setExistingFiles(files);
                setFileSlots([]);
                setSlotCounter(0);
            } else {
                setTitle("");
                setText("");
                setStatus(lastStatus ?? "");
                setExistingFiles([]);
                setFileSlots([{ id: 0, file: null }]);
                setSlotCounter(1);
            }
        }
    }, [open, isEdit, annotation]);

    const handleAddFileSlot = useCallback(() => {
        setFileSlots((prev) => [...prev, { id: slotCounter, file: null }]);
        setSlotCounter((c) => c + 1);
    }, [slotCounter]);

    const handleRemoveFileSlot = useCallback((slotId) => {
        setFileSlots((prev) => prev.filter((s) => s.id !== slotId));
    }, []);

    const handleRemoveExistingFile = useCallback((filePk) => {
        setExistingFiles((prev) => prev.filter((f) => f.pk !== filePk));
    }, []);

    const handlePreview = useCallback((af) => {
        setPreviewFile(af);
        setPreviewModalOpen(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewModalOpen(false);
        setPreviewFile(null);
    }, []);

    const handleDownload = useCallback((af) => {
        downloadFile(af.file_pk, af.original_filename);
    }, []);

    const handleFileSelect = useCallback((slotId, file) => {
        setFileSlots((prev) =>
            prev.map((s) => (s.id === slotId ? { ...s, file } : s))
        );
    }, []);

    const uploadFiles = useCallback(async () => {
        const filePks = [];
        const filesToUpload = fileSlots.filter((s) => s.file);

        for (const slot of filesToUpload) {
            const res = await fetchUrl(
                "/notice_annotation/file_upload",
                "POST",
                true,
                slot.file,
                false,
                true,
                null,
                null,
                "multipart/form-data"
            );
            if (
                res.status === HTTP_STATUS.CREATED_201 ||
                res.status === HTTP_STATUS.OK_200
            ) {
                const pk = res.data?.pk || res.data?.data?.pk;
                if (pk) filePks.push(pk);
            }
        }
        return filePks;
    }, [fileSlots]);

    const handleSave = useCallback(async () => {
        if (!title.trim() || !text || text === "<p><br></p>" || text.replace(/<[^>]*>/g, "").trim() === "" || status === "") return;

        if (FINAL_STATUSES.includes(Number(status))) {
            const result = await Swal.fire({
                icon: "warning",
                title: intl.get("FinalizeNoticeTitle").d("Finalize notice?"),
                text: intl.get("FinalizeNoticeMessage").d("This notice will be finalized and can no longer be edited after this action. Are you sure?"),
                showCancelButton: true,
                confirmButtonText: intl.get("Yes").d("Yes"),
                cancelButtonText: intl.get("No").d("No"),
                confirmButtonColor: "#d33",
            });
            if (!result.isConfirmed) return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                const newFilePks = await uploadFiles();
                const keptFilePks = existingFiles.map((f) => f.file_pk);
                const allFilePks = [...keptFilePks, ...newFilePks];

                await fetchData(`/notice_annotation/${annotation.pk}`, "PUT", true, {
                    title,
                    text,
                    status: Number(status),
                    file_pks: allFilePks,
                });
            } else {
                const filePks = await uploadFiles();
                await fetchData("/notice_annotation", "POST", true, {
                    title,
                    text,
                    status: Number(status),
                    notice_pk: Number(noticePk),
                    file_pks: filePks,
                });
            }
            onSaved();
        } catch (error) {
            console.error("Failed to save annotation:", error);
        } finally {
            setSaving(false);
        }
    }, [title, text, status, isEdit, annotation, noticePk, existingFiles, uploadFiles, onSaved]);

    return (
        <>
        <DmsModal isVisible={open} onClose={onClose} maxWidth="md" fullWidth>
            <DmsModal.Header
                title={
                    isEdit
                        ? intl.get("EditAnnotation").d("Edit Annotation")
                        : intl.get("AddAnnotation").d("Add Annotation")
                }
                onClose={onClose}
            />
            <DmsModal.Body>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
                    <TextField
                        label={intl.get("AnnotationTitle").d("Title")}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                    />
                    <DmsRichTextEditor
                        key={editorKey}
                        label={intl.get("AnnotationText").d("Text")}
                        value={text}
                        onChange={setText}
                        placeholder={intl.get("AnnotationTextPlaceholder").d("Write the annotation text...")}
                        disabled={saving}
                    />
                    <FormControl fullWidth required disabled={saving}>
                        <InputLabel>{intl.get("AnnotationStatus").d("Status")}</InputLabel>
                        <Select
                            value={status}
                            label={intl.get("AnnotationStatus").d("Status")}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            {statusOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                            <Typography variant="subtitle2">
                                {intl.get("AttachedFiles").d("Attached Files")}
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={handleAddFileSlot}
                                disabled={saving}
                            >
                                {intl.get("AddFile").d("Add File")}
                            </Button>
                        </Box>

                        {isEdit && existingFiles.length > 0 && (
                            <List dense disablePadding>
                                {existingFiles.map((ef) => (
                                    <ListItem
                                        key={`existing-${ef.pk}`}
                                        sx={{
                                            bgcolor: "background.paper",
                                            borderRadius: 1,
                                            mb: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                        secondaryAction={
                                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                                <Tooltip title={intl.get("Preview").d("Preview")}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handlePreview(ef)}
                                                        disabled={!ef.file_pk || saving}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={intl.get("Download").d("Download")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDownload(ef)}
                                                        disabled={!ef.file_pk || saving}
                                                    >
                                                        <DownloadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={intl.get("Delete").d("Delete")}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveExistingFile(ef.pk)}
                                                        disabled={saving}
                                                    >
                                                        <DeleteOutlineIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                    >
                                        <ListItemIcon>
                                            <DescriptionIcon color="action" />
                                        </ListItemIcon>
                                        <ListItemText primary={ef.original_filename} />
                                    </ListItem>
                                ))}
                            </List>
                        )}

                        {fileSlots.map((slot) => (
                            <Box
                                key={slot.id}
                                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <DocumentUploadSlot
                                        docName={intl.get("SelectFile").d("Select File")}
                                        file={slot.file}
                                        onFileSelect={(_, file) => handleFileSelect(slot.id, file)}
                                        disabled={saving}
                                    />
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveFileSlot(slot.id)}
                                    disabled={saving}
                                    color="error"
                                >
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}

                        {existingFiles.length === 0 && fileSlots.length === 0 && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1 }}>
                                {intl.get("NoFilesAttached").d("No files attached")}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <DmsButton
                    onClick={onClose}
                    text={intl.get("Cancel").d("Cancel")}
                    disabled={saving}
                />
                <DmsButton
                    onClick={handleSave}
                    variant="primary"
                    text={intl.get("Save").d("Save")}
                    disabled={saving || !title.trim() || !text || text === "<p><br></p>" || text.replace(/<[^>]*>/g, "").trim() === "" || status === ""}
                />
            </DmsModal.Footer>
        </DmsModal>

        <DmsFileViewerModal
            isOpen={previewModalOpen}
            onClose={handleClosePreview}
            filePk={previewFile?.file_pk}
            fileName={previewFile?.original_filename}
            title={previewFile?.original_filename}
        />
        </>
    );
};

AnnotationFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
    noticePk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    annotation: PropTypes.object,
    lastStatus: PropTypes.number,
    statusOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.number.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
};
